import React, { useState, useEffect, useMemo, useCallback } from 'react';
import CardDetail from './CardDetail.js';
import { getFromStorage, saveToStorage } from '../utils/localStorage.js';
import CostCurveChart from './CostCurveChart.js';
import { getCardName, getCardTypeName, getUILoc } from '../utils/cardUtils.js';
import { CardIllustration } from './Card.js';
import audioManager from '../utils/AudioManager.js';
import PlayRecordModal from './overlays/PlayRecordModal.js';
import './CostCurveChart.css';

const DeckSelectionScreen = ({ presetDecks, cardDefs, onDeckSelected, onBack, onScreenChange }) => {
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [showDeckPreview, setShowDeckPreview] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [customDecks, setCustomDecks] = useState({});
  const [costCurveMode, setCostCurveMode] = useState('single'); // 'single' or 'grouped'
  const [deleteConfirm, setDeleteConfirm] = useState(null); // 削除確認対象のデッキ名
  const [showPlayRecordModal, setShowPlayRecordModal] = useState(false);

  useEffect(() => {
    setCustomDecks(getFromStorage('customDecks') || {});
  }, []);

  const allDecks = useMemo(() => {
    const combined = [
      ...presetDecks.map(deck => ({ ...deck, type: 'PRESET', isValid: deck.cards.length >= 30 })),
      ...Object.values(customDecks).map(deck => ({ ...deck, type: 'CUSTOM', isValid: deck.cards.length >= 30 }))
    ];
    return combined;
  }, [presetDecks, customDecks]);

  const handleDeckSelect = (deck) => {
    audioManager.playSE('select');
    setSelectedDeck(deck);
    setShowDeckPreview(true);
  };

  const handleConfirmDeck = useCallback(() => {
    if (!selectedDeck) {
      audioManager.playSE('cancel');
      alert(getUILoc('MESSAGES.SELECT_DECK_ALERT'));
      return;
    }
    if (!selectedDeck.isValid) {
      audioManager.playSE('cancel');
      alert(getUILoc('MESSAGES.DECK_SIZE_ERROR'));
      return;
    }
    if (!selectedDeck.cards || selectedDeck.cards.length === 0) {
      audioManager.playSE('cancel');
      alert(getUILoc('MESSAGES.EMPTY_DECK_ALERT'));
      return;
    }
    audioManager.playSE('select');
    onDeckSelected(selectedDeck);
  }, [selectedDeck, onDeckSelected]);

  const handleCardClick = useCallback((cardIdOrName) => {
    audioManager.playSE('select');
    const cardDef = cardDefs[cardIdOrName] || Object.values(cardDefs).find(def => def.name === cardIdOrName);
    if (cardDef) {
      setSelectedCard(cardDef);
    }
  }, [cardDefs]);

  const handleDeleteDeck = useCallback((deckName, event) => {
    event.stopPropagation();
    event.preventDefault();
    audioManager.playSE('select');
    // window.confirmの代わりにインライン確認UIを使用（PCでのブロック対策）
    setDeleteConfirm(deckName);
  }, []);

  const handleDeleteConfirmed = useCallback(() => {
    if (!deleteConfirm) return;
    audioManager.playSE('select');
    const newCustomDecks = { ...customDecks };
    delete newCustomDecks[deleteConfirm];
    saveToStorage('customDecks', newCustomDecks);
    setCustomDecks(newCustomDecks);
    setDeleteConfirm(null);
  }, [deleteConfirm, customDecks]);

  const handleDeleteCancelled = useCallback(() => {
    audioManager.playSE('cancel');
    setDeleteConfirm(null);
  }, []);

  const handleEditDeck = (deck, event) => {
    event.stopPropagation();
    audioManager.playSE('select');
    onScreenChange('deckBuilder', { deckToEdit: deck });
  };

  const handleBackToGrid = () => {
    audioManager.playSE('cancel');
    setShowDeckPreview(false);
  };

  const handleBackToTitle = () => {
    audioManager.playSE('cancel');
    onBack();
  };

  const handleTutorial = () => {
    audioManager.playSE('select');
    onScreenChange('tutorialSelection');
  };

  const toggleCostCurveMode = () => {
    audioManager.playSE('select');
    setCostCurveMode(costCurveMode === 'single' ? 'grouped' : 'single');
  };

  const renderDeckPreview = () => {
    if (!selectedDeck || !showDeckPreview) return null;

    const deckCards = selectedDeck.cards.reduce((acc, cardId) => {
      acc[cardId] = (acc[cardId] || 0) + 1;
      return acc;
    }, {});

    const previewCostCurveData = (() => {
      const counts = {};
      selectedDeck.cards.forEach(cardIdOrName => {
        const cardDef = cardDefs[cardIdOrName] || Object.values(cardDefs).find(def => def.name === cardIdOrName);
        if (cardDef) {
          const cost = cardDef.required_scale;
          counts[cost] = (counts[cost] || 0) + 1;
        }
      });

      if (costCurveMode === 'single') {
        const maxCost = Math.max(...Object.keys(counts).map(Number), 0);
        const data = [];
        for (let i = 0; i <= maxCost; i++) {
          if (counts[i] > 0) {
            data.push({ label: `${i}`, count: counts[i] });
          }
        }
        return data;
      } else { // grouped
        const groups = { '0-4': 0, '5-9': 0, '10-14': 0, '15-19': 0, '20+': 0 };
        Object.entries(counts).forEach(([cost, count]) => {
          const c = parseInt(cost);
          if (c <= 4) groups['0-4'] += count;
          else if (c <= 9) groups['5-9'] += count;
          else if (c <= 14) groups['10-14'] += count;
          else if (c <= 19) groups['15-19'] += count;
          else groups['20+'] += count;
        });
        return Object.entries(groups).map(([label, count]) => ({ label, count }));
      }
    })();

    return (
      <div className="deck-preview">
        <h3>{getUILoc(selectedDeck.name)} {!selectedDeck.isValid && `(${getUILoc('LABELS.INVALID_DECK_TAG')})`}</h3>
        <p className="deck-description">{getUILoc(selectedDeck.description) || `${getUILoc(`LABELS.${selectedDeck.type}`)}${getUILoc('LABELS.DECK')}`}</p>

        <div className="deck-cards">
          <h4>{getUILoc('LABELS.DECK_CONTENT')} ({getUILoc('UNITS.CARDS', { count: selectedDeck.cards.length })})</h4>
          <div className="card-list">
            {Object.entries(deckCards).map(([cardIdOrName, count]) => {
              const cardDef = cardDefs[cardIdOrName] || Object.values(cardDefs).find(def => def.name === cardIdOrName);
              if (!cardDef) return null;

              return (
                <div 
                  key={cardIdOrName} 
                  className="deck-card-item"
                  onClick={() => handleCardClick(cardIdOrName)}
                >
                  <CardIllustration cardId={cardDef.id} isMini={true} />
                  <span className="card-count">×{count}</span>
                  <span className="card-name">{getCardName(cardDef.id)}</span>
                  <span className="card-type">({getCardTypeName(cardDef.card_type)})</span>
                  <span className="card-scale">{getUILoc('LABELS.REQUIRED_SCALE')}:{cardDef.required_scale}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="cost-curve-wrapper">
          <div className="cost-curve-header">
            <h3>{getUILoc('LABELS.SCALE_DISTRIBUTION')}</h3>
            <button onClick={toggleCostCurveMode}>
              {getUILoc('LABELS.SWITCH_DISPLAY')} ({costCurveMode === 'single' ? getUILoc('LABELS.STEP_1') : getUILoc('LABELS.STEP_5')})
            </button>
          </div>
          <CostCurveChart data={previewCostCurveData} />
        </div>

        <div className="deck-actions">
          <button className="back-button" onClick={handleBackToGrid}>{getUILoc('BUTTONS.BACK')}</button>
          <button onClick={handleConfirmDeck} className="confirm-button" disabled={!selectedDeck.isValid}>
            {getUILoc('BUTTONS.START_WITH_DECK')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="deck-selection-screen">
      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="choice-prompt-overlay" onClick={handleDeleteCancelled}>
          <div className="choice-prompt" onClick={(e) => e.stopPropagation()}>
            <h3>{getUILoc('MESSAGES.DELETE_DECK_CONFIRM', { name: deleteConfirm })}</h3>
            <div className="choice-options">
              <button className="surrender-confirm-yes" onClick={handleDeleteConfirmed}>
                {getUILoc('BUTTONS.YES')}
              </button>
              <button onClick={handleDeleteCancelled}>
                {getUILoc('BUTTONS.NO')}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="screen-header">
        <div className="header-left">
          <h1>{getUILoc('SCREENS.DECK_SELECTION')}</h1>
          <button className="tutorial-button" onClick={handleTutorial}>{getUILoc('BUTTONS.TUTORIAL')}</button>
        </div>
        <button className="back-button" onClick={handleBackToTitle}>← {getUILoc('BUTTONS.BACK')}</button>
      </div>

      {!showDeckPreview ? (
        <div className="deck-selection-area">
          <h2>{getUILoc('MESSAGES.SELECT_DECK_PROMPT')}</h2>
          <div className="play-record-trigger-container">
            <button
              className="play-record-trigger-button"
              onClick={() => {
                audioManager.playSE('select');
                setShowPlayRecordModal(true);
              }}
            >
              {getUILoc('BUTTONS.PLAY_RECORD')}
            </button>
          </div>
          <div className="deck-grid">
            {allDecks.map((deck) => (
              <div 
                key={deck.name} 
                className={`deck-card ${!deck.isValid ? 'invalid' : ''}`}
                onClick={() => handleDeckSelect(deck)}
              >
                <span className={`deck-type-badge ${deck.type.toLowerCase()}`}>{getUILoc(`LABELS.${deck.type}`)}</span>
                <h3>{getUILoc(deck.name)}</h3>
                <p className="deck-description">{getUILoc(deck.description)}</p>
                <div className="deck-info">
                  <span>{getUILoc('LABELS.CARD_COUNT')}: {deck.cards.length}</span>
                  {!deck.isValid && <span className="invalid-tag">{getUILoc('STATUS.INVALID_DECK')}（{getUILoc('LABELS.VALID_DECK_HINT')}）</span>}
                </div>
                {deck.type === 'CUSTOM' && (
                  <div className="custom-deck-actions">
                    <button className="edit-button" onClick={(e) => handleEditDeck(deck, e)}>{getUILoc('BUTTONS.EDIT')}</button>
                    <button className="delete-button" onClick={(e) => handleDeleteDeck(deck.name, e)}>{getUILoc('BUTTONS.DELETE')}</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        renderDeckPreview()
      )}

      {selectedCard && (
        <CardDetail
          card={selectedCard}
          cardDefs={Object.values(cardDefs)}
          onClose={() => {
            audioManager.playSE('cancel');
            setSelectedCard(null);
          }}
        />
      )}

      <PlayRecordModal
        isOpen={showPlayRecordModal}
        onClose={() => setShowPlayRecordModal(false)}
      />
    </div>
  );
};

export default DeckSelectionScreen;
